package com.siriusxm.unifiedcontent.console.player

import androidx.compose.runtime.Composable
import com.siriusxm.pia.player.AudioTrack
import com.siriusxm.pia.player.PlaybackDetailsState
import com.siriusxm.pia.player.PlaybackDetailsStatus
import contentingestion.unifiedmodel.ReferenceIdentifiable
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

/**
 * The expanded now-playing surface separates the changing cut from the anchored channel/episode.
 * Related catalog links belong to that current cut, while source artwork and title remain stable.
 * Missing clocks, metadata gaps, and lookup failures remain explicit without replacing the source.
 */
@Composable
internal fun PlaybackDetailsPanel(
    state: PlaybackDetailsState,
    source: AudioTrack,
    resolveName: suspend (ReferenceIdentifiable) -> String,
    entityHref: (ReferenceIdentifiable) -> String?,
    names: MutableMap<ReferenceIdentifiable, String>,
    onCollapse: () -> Unit,
) {
    Div({
        id("player-playback-details")
        classes(AudioPlayerStyles.detailsPanel)
        attr("role", "region")
        attr("aria-label", "Playback details")
        attr("aria-live", "polite")
    }) {
        PlayerDetailsToggle(true, onCollapse)
        Div({ classes(AudioPlayerStyles.detailsColumns) }) {
            Div({ classes(AudioPlayerStyles.cutIdentity) }) {
                val current = state.current
                PlayerArtwork(current?.imageUrl, AudioPlayerStyles.cutArtwork)
                Div {
                    Div({ classes(AudioPlayerStyles.detailEyebrow) }) {
                        Text(if (current?.subtitle != null) "NOW PLAYING · TRACK" else "NOW PLAYING")
                    }
                    Div({ classes(AudioPlayerStyles.detailsTitle) }) { Text(current?.title ?: "Now playing") }
                    current?.subtitle?.let { Div({ classes(AudioPlayerStyles.detailsArtist) }) { Text(it) } }
                    if (current?.approximate == true) {
                        Div({ classes(AudioPlayerStyles.detailsNote) }) { Text("Live details may differ slightly from the audio.") }
                    }
                }
            }
            Div({ classes(AudioPlayerStyles.contextIdentity) }) {
                PlayerArtwork(source.imageUrl, AudioPlayerStyles.contextArtwork)
                Div {
                    state.current?.let { RelatedPlaybackEntities(it.relatedEntities, resolveName, names, entityHref) }
                    Div({ classes(AudioPlayerStyles.contextTitle) }) { Text(source.title ?: "Audio") }
                    Div({ classes(AudioPlayerStyles.contextCaption) }) { Text(sourceCaption(source)) }
                }
            }
        }
        val message = when (state.status) {
            PlaybackDetailsStatus.LOADING -> "Loading playback details…"
            PlaybackDetailsStatus.WAITING_FOR_CLOCK -> "Waiting to synchronize details with the audio…"
            PlaybackDetailsStatus.EMPTY -> "No playback details are available for this point in the audio."
            PlaybackDetailsStatus.UNAVAILABLE -> "Playback details are temporarily unavailable. Retrying…"
            PlaybackDetailsStatus.UNSUPPORTED -> "Playback details are not available for this media."
            PlaybackDetailsStatus.AVAILABLE -> null
        }
        message?.let { Div({ classes(AudioPlayerStyles.detailsNote) }) { Text(it) } }
    }
}
